import { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Search, X, ChevronsUpDown, CheckCircle, AlertTriangle, Loader2, Banknote } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort, formatDateFromIso } from '@/lib/datetime';

export default function OwnerPayments() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
    const customerDropdownRef = useRef(null);
    const [form, setForm] = useState({ customer_id: '', amount: '', method: 'cash', status: 'completed', reference: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Cash promises state
    const [cashPromises, setCashPromises] = useState([]);
    const [transferPromises, setTransferPromises] = useState([]);
    const [promisesLoading, setPromisesLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null); // payment id being acted on

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/owner/payments', { params: { status: statusFilter, method: methodFilter, page, per_page: 10 } });
            setPayments(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [statusFilter, methodFilter, page]);

    const fetchCashPromises = useCallback(async () => {
        setPromisesLoading(true);
        try {
            const res = await api.get('/owner/cash-promises');
            setCashPromises(res.data.data || []);
        } catch {} finally { setPromisesLoading(false); }
    }, []);

    const fetchTransferPromises = useCallback(async () => {
        setPromisesLoading(true);
        try {
            const res = await api.get('/owner/transfer-promises');
            setTransferPromises(res.data.data || []);
        } catch {} finally { setPromisesLoading(false); }
    }, []);

    useEffect(() => { fetchPayments(); }, [fetchPayments]);
    useEffect(() => { fetchCashPromises(); }, [fetchCashPromises]);
    useEffect(() => { fetchTransferPromises(); }, [fetchTransferPromises]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) {
                setCustomerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleConfirmCash = async (id) => {
        setActionLoading(id);
        try {
            await api.post(`/owner/cash-promises/${id}/confirm`);
            fetchCashPromises();
            fetchPayments();
        } catch {} finally { setActionLoading(null); }
    };

    const handleRevokeCash = async (id) => {
        if (!confirm('¿Revocar esta promesa de pago? El alumno podría perder el acceso.')) return;
        setActionLoading(id);
        try {
            await api.post(`/owner/cash-promises/${id}/revoke`);
            fetchCashPromises();
            fetchPayments();
        } catch {} finally { setActionLoading(null); }
    };

    const handleConfirmTransfer = async (id) => {
        setActionLoading(id);
        try {
            await api.post(`/owner/transfer-promises/${id}/confirm`);
            fetchTransferPromises();
            fetchPayments();
        } catch {} finally { setActionLoading(null); }
    };

    const handleRevokeTransfer = async (id) => {
        if (!confirm('¿Rechazar esta transferencia? El alumno podría perder el acceso.')) return;
        setActionLoading(id);
        try {
            await api.post(`/owner/transfer-promises/${id}/revoke`);
            fetchTransferPromises();
            fetchPayments();
        } catch {} finally { setActionLoading(null); }
    };

    const selectedCustomer = customers.find(c => String(c.id) === String(form.customer_id));

    const filteredCustomers = customers.filter(c => {
        if (!customerSearch) return true;
        const q = customerSearch.toLowerCase();
        return `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) || (c.dni && c.dni.includes(q));
    });

    const openCreate = async () => {
        setForm({ customer_id: '', amount: '', method: 'cash', status: 'completed', reference: '', notes: '' });
        setCustomerSearch('');
        setCustomerDropdownOpen(false);
        setError('');
        try {
            const res = await api.get('/owner/customers?per_page=200');
            setCustomers(res.data.data);
        } catch {}
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.post('/owner/payments', { ...form, amount: parseFloat(form.amount), customer_id: form.customer_id || null });
            setDialogOpen(false);
            fetchPayments();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const statusBadge = (status) => {
        const labels = { pending: 'Pendiente', completed: 'Completado', failed: 'Fallido', refunded: 'Reembolsado' };
        const isCompleted = status === 'completed';
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isCompleted ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>
                {labels[status] || status}
            </span>
        );
    };

    const methodLabel = (m) => m === 'cash' ? 'Efectivo' : 'MercadoPago';

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Pagos</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Registrar Pago</Button>
            </div>

            {/* Pending Cash Promises Section */}
            {cashPromises.length > 0 && (
                <Card className="bg-[#0a0a0a] border-yellow-900/50 mb-6">
                    <CardHeader>
                        <div className="flex flex-wrap items-center gap-2">
                            <Banknote className="h-5 w-5 text-yellow-400" />
                            <h2 className="text-sm font-bold uppercase tracking-wider text-yellow-400">Promesas de Pago en Efectivo</h2>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-300">
                                {cashPromises.length} pendiente{cashPromises.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {cashPromises.map(p => (
                                <div key={p.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border border-yellow-900/30 bg-yellow-950/10">
                                    <div className="min-w-0 w-full sm:w-auto">
                                        <p className="font-medium text-white text-sm">{p.customer ? `${p.customer.first_name} ${p.customer.last_name}` : 'Alumno desconocido'}</p>
                                        <p className="text-xs text-white/60">{p.gym?.name || ''} — ${p.amount}</p>
                                        <p className="text-xs text-yellow-400/70 mt-0.5">{p.notes}</p>
                                        <p className="text-[10px] text-white/40 mt-0.5">{formatDateTimeShort(p.created_at)}</p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto sm:shrink-0 sm:ml-3">
                                        <Button
                                            size="sm"
                                            className="w-full sm:w-auto uppercase tracking-widest text-[10px] bg-green-900/50 hover:bg-green-800/50 text-green-300 border-green-800/50"
                                            disabled={actionLoading === p.id}
                                            onClick={() => handleConfirmCash(p.id)}
                                        >
                                            {actionLoading === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                                            Confirmar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="w-full sm:w-auto uppercase tracking-widest text-[10px] border-red-900/50 text-red-400 hover:bg-red-950"
                                            disabled={actionLoading === p.id}
                                            onClick={() => handleRevokeCash(p.id)}
                                        >
                                            <AlertTriangle className="h-3 w-3 mr-1" />
                                            Revocar
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pending Transfer Promises Section */}
            {transferPromises.length > 0 && (
                <Card className="bg-[#0a0a0a] border-blue-900/50 mb-6">
                    <CardHeader>
                        <div className="flex flex-wrap items-center gap-2">
                            <Banknote className="h-5 w-5 text-blue-400" />
                            <h2 className="text-sm font-bold uppercase tracking-wider text-blue-400">Transferencias Pendientes</h2>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300">
                                {transferPromises.length} pendiente{transferPromises.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {transferPromises.map(p => (
                                <div key={p.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border border-blue-900/30 bg-blue-950/10">
                                    <div className="min-w-0 w-full sm:w-auto">
                                        <p className="font-medium text-white text-sm">{p.customer ? `${p.customer.first_name} ${p.customer.last_name}` : 'Alumno desconocido'}</p>
                                        <p className="text-xs text-white/60">{p.gym?.name || ''} — ${p.amount}</p>
                                        <p className="text-xs text-blue-400/70 mt-0.5">{p.notes}</p>
                                        <p className="text-[10px] text-white/40 mt-0.5">{formatDateTimeShort(p.created_at)}</p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto sm:shrink-0 sm:ml-3">
                                        <Button
                                            size="sm"
                                            className="w-full sm:w-auto uppercase tracking-widest text-[10px] bg-green-900/50 hover:bg-green-800/50 text-green-300 border-green-800/50"
                                            disabled={actionLoading === p.id}
                                            onClick={() => handleConfirmTransfer(p.id)}
                                        >
                                            {actionLoading === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                                            Confirmar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="w-full sm:w-auto uppercase tracking-widest text-[10px] border-red-900/50 text-red-400 hover:bg-red-950"
                                            disabled={actionLoading === p.id}
                                            onClick={() => handleRevokeTransfer(p.id)}
                                        >
                                            <AlertTriangle className="h-3 w-3 mr-1" />
                                            Rechazar
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                        <select className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                            <option value="" className="bg-[#0a0a0a] text-white">Todos los estados</option>
                            <option value="pending" className="bg-[#0a0a0a] text-white">Pendiente</option>
                            <option value="completed" className="bg-[#0a0a0a] text-white">Completado</option>
                            <option value="failed" className="bg-[#0a0a0a] text-white">Fallido</option>
                            <option value="refunded" className="bg-[#0a0a0a] text-white">Reembolsado</option>
                        </select>
                        <select className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white" value={methodFilter} onChange={e => { setMethodFilter(e.target.value); setPage(1); }}>
                            <option value="" className="bg-[#0a0a0a] text-white">Todos los métodos</option>
                            <option value="cash" className="bg-[#0a0a0a] text-white">Efectivo</option>
                            <option value="mercadopago" className="bg-[#0a0a0a] text-white">MercadoPago</option>
                        </select>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : payments.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron pagos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[600px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Fecha</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Alumno</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Monto</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Método</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Estado</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Referencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map(p => (
                                            <tr key={p.id} className="border-b border-[#3a3a3f] last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 text-white/60">{formatDateFromIso(p.created_at)}</td>
                                                <td className="py-3 px-2 text-white/60">{p.customer ? `${p.customer.first_name} ${p.customer.last_name}` : '-'}</td>
                                                <td className="py-3 px-2 font-semibold text-white">${p.amount}</td>
                                                <td className="py-3 px-2 text-white/60">{methodLabel(p.method)}</td>
                                                <td className="py-3 px-2">{statusBadge(p.status)}</td>
                                                <td className="py-3 px-2 text-white/60">{p.reference || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">Registrar Pago</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Alumno (opcional)</Label>
                            <div className="relative" ref={customerDropdownRef}>
                                {selectedCustomer && !customerDropdownOpen ? (
                                    <div className="flex h-9 w-full items-center justify-between rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm">
                                        <span className="truncate text-white">{selectedCustomer.first_name} {selectedCustomer.last_name} — {selectedCustomer.dni}</span>
                                        <div className="flex items-center gap-1 shrink-0 ml-2">
                                            <button type="button" onClick={() => { setForm({ ...form, customer_id: '' }); setCustomerSearch(''); }} className="text-white/60 hover:text-white">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                            <button type="button" onClick={() => setCustomerDropdownOpen(true)} className="text-white/60 hover:text-white">
                                                <ChevronsUpDown className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/60" />
                                        <Input
                                            placeholder="Buscar por nombre o DNI..."
                                            value={customerSearch}
                                            onChange={e => { setCustomerSearch(e.target.value); setCustomerDropdownOpen(true); }}
                                            onFocus={() => setCustomerDropdownOpen(true)}
                                            className="pl-8 h-9 bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white"
                                        />
                                    </div>
                                )}
                                {customerDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] max-h-[180px] overflow-y-auto">
                                        <button
                                            type="button"
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-white ${!form.customer_id ? 'bg-white/5 font-medium' : ''}`}
                                            onClick={() => { setForm({ ...form, customer_id: '' }); setCustomerSearch(''); setCustomerDropdownOpen(false); }}
                                        >
                                            Sin alumno asociado
                                        </button>
                                        {filteredCustomers.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-white/60">Sin resultados</div>
                                        ) : (
                                            filteredCustomers.map(c => (
                                                <button
                                                    type="button"
                                                    key={c.id}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 text-white ${String(form.customer_id) === String(c.id) ? 'bg-white/5 font-medium' : ''}`}
                                                    onClick={() => { setForm({ ...form, customer_id: String(c.id) }); setCustomerSearch(''); setCustomerDropdownOpen(false); }}
                                                >
                                                    {c.first_name} {c.last_name} <span className="text-white/60">— {c.dni}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Monto</Label>
                                <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Método</Label>
                                <select className="flex h-9 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
                                    <option value="cash" className="bg-[#0a0a0a] text-white">Efectivo</option>
                                    <option value="mercadopago" className="bg-[#0a0a0a] text-white">MercadoPago</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Estado</Label>
                                <select className="flex h-9 w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-1 text-sm text-white" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    <option value="completed" className="bg-[#0a0a0a] text-white">Completado</option>
                                    <option value="pending" className="bg-[#0a0a0a] text-white">Pendiente</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Referencia</Label>
                                <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Nro. de comprobante" className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Notas</Label>
                            <textarea className="flex min-h-[60px] w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Registrar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
